import { FC, useState } from 'react';
import { Select, MenuItem, FormControl, SelectChangeEvent } from '@mui/material';
import { observer } from 'mobx-react';

import { apiService } from '../../../services/apiService';
import useNotifier from '../../../hooks/useNotifier';
import { useClaimsStore } from '../../../hooks/useClaimsStore';

interface AssignedUserSelectProps {
    claimId: string;
    currentUser: string;
}

const AssignedUserSelect: FC<AssignedUserSelectProps> = ({ 
    claimId, 
    currentUser, 
}) => {
    const { assignedUsers: _assignedUsers, isOptionsLoading } = useClaimsStore();
    const notifier = useNotifier();
    
    const assignedUsers = _assignedUsers.map(user => ({ value: user.label, label: user.label, id: user.value }));
    
    const [selectedUser, setSelectedUser] = useState(currentUser || '');
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = async (event: SelectChangeEvent<string>) => {
        event.stopPropagation();
        const newUser = event.target.value;
        const previousUser = selectedUser;
        
        setSelectedUser(newUser);
        setIsLoading(true);

        const selectedUserId = assignedUsers?.find(user => user.value === newUser)?.id;

        try {
            await apiService.updateClaimAssignedUser(claimId, selectedUserId || '');
            notifier.showSuccess('Assigned user updated successfully');
        } catch (error) {
            console.error('Failed to update assigned user:', error);
            notifier.showError('Failed to update assigned user');
            // Revert to previous value on error
            setSelectedUser(previousUser);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClick = (event: React.MouseEvent) => {
        event.stopPropagation();
    };

    return (
        <FormControl 
            size="small" 
            onClick={handleClick}
            sx={{ width: { xs: '100px', sm: '110px', md: '115px', lg: '125px' } }}
        >
            <Select
                value={selectedUser || ''}
                onChange={handleChange}
                variant="outlined"
                disabled={isLoading || isOptionsLoading}
                sx={(theme) => ({
                    border: 'none !important',
                    color: 'text.primary',
                    textAlign: 'left',
                    borderRadius: '4px',
                    height: { xs: '26px !important', sm: '28px !important', md: '28px !important', lg: '30px !important' },
                    minHeight: { xs: '26px !important', sm: '28px !important', md: '28px !important', lg: '30px !important' },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: isLoading ? theme.palette.divider : theme.palette.action.disabled,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.action.disabled,
                        borderWidth: '1px',
                    },
                    '& .MuiSelect-select': {
                        py: 0,
                        pr: 2,
                        pl: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minHeight: 'unset',
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                    },
                    '& .MuiSelect-icon': {
                        color: 'text.secondary',
                        right: 2,
                        fontSize: '0.875rem',
                    },
                    '&.Mui-disabled': {
                        opacity: 0.6,
                    },
                })}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            maxHeight: 250,
                            '& .MuiMenuItem-root': {
                                // fontSize: 'var(--font-size-xs)',
                                minHeight: 28,
                                py: 0.5,
                            },
                        },
                    },
                }}
            >
                {assignedUsers?.map((user) => (
                    <MenuItem key={user.value} value={user.value}>
                        {user.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default observer(AssignedUserSelect);

